/* eslint-disable max-lines */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, of, sampleTime, switchMap } from 'rxjs';
import { buffer, catchError, map } from 'rxjs/operators';

import { UserActions } from '@recruitee/account/data';
import { FullCandidate } from '@recruitee/http/types';
import { referencesToActionsFactory } from '@recruitee/ngrx';
import { JobsBriefActions, StageActions } from '@recruitee/offers/data';
import { PlacementActions } from '@recruitee/placements/data';
import { CandidateReferrerActions } from '@recruitee/referrals/data';

import { FullCandidateHttpClient, FullCandidateReferences } from './full-candidate-http-client';
import { FullCandidateActions } from './full-candidate.store';

@Injectable({ providedIn: 'root' })
export class FullCandidateEffects {
  public fetchOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.fetchOne),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.fetchOne(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.fetchOneSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.fetchOneFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public bufferedFetchOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.bufferedFetchOne),
      map(({ payload }) => payload.id),
      buffer(
        this.actions$.pipe(ofType(FullCandidateActions.bufferedFetchOne)).pipe(sampleTime(100)),
      ),
      mergeMap(ids => [...new Set(ids)]),
      mergeMap(id =>
        this.fullCandidateHttpClient.fetchOne(id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.fetchOneSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.fetchOneFailed({ payload: { error, id } })),
          ),
        ),
      ),
    ),
  );

  public create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.createOnePessimistic),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.create(payload.params).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),

            FullCandidateActions.createOnePessimisticSuccess({
              payload: { data: candidate, correlationId: payload.correlationId },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              FullCandidateActions.createOnePessimisticFailed({
                payload: { error, correlationId: payload.correlationId },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.updateOnePessimistic),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.update(payload.params).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),

            FullCandidateActions.updateOnePessimisticSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              FullCandidateActions.updateOnePessimisticFailed({
                payload: { error, data: { id: payload.params.data.id! } },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public removePhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.removePhoto),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.removePhoto(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.removePhotoSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.removePhotoFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public updateCv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.updateCv),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.updateCv(payload).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.updateCvSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.updateCvFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public updateParseCv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.updateParseCv),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.updateParseCv(payload).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.updateParseCvSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.updateParseCvFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public deleteCv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.deleteCv),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.deleteCv(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.deleteCvSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.deleteCvFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public deleteParsedCv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.deleteParsedCv),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.deleteParsedCv(payload).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.deleteParsedCvSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.deleteParsedCvFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public reparseCv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.reparseCv),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.reparseCv(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.reparseCvSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.reparseCvFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public view$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.view),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.view(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.viewSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.viewFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public merge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.merge),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.merge(payload.id).pipe(
          switchMap(data => [FullCandidateActions.mergeSuccess({ payload: { uuid: data.uuid } })]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.mergeFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public follow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.follow),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.follow(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.followSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.followFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public unfollow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.unfollow),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.unfollow(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.unfollowSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.unfollowFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public retrieve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.retrieve),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.retrieve(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.retrieveSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.retrieveFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public copyToCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.copyToCompany),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.copyToCompany(payload.params).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.copyToCompanySuccess({
              payload: { data: candidate, correlationId: payload.correlationId },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              FullCandidateActions.copyToCompanyFailed({
                payload: { error, correlationId: payload.correlationId },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private fullCandidateHttpClient: FullCandidateHttpClient,
  ) {}

  private getReferencesActions(candidate: FullCandidate, references: FullCandidateReferences[]) {
    const referenceActions = referencesToActionsFactory<FullCandidateReferences>()([
      { type: 'Admin', action: UserActions.references },
      { type: 'Stage', action: StageActions.references },
      { type: 'Offer', action: JobsBriefActions.references },
      { type: 'ReferralReferrer', action: CandidateReferrerActions.references },
    ]);

    const placementActions = candidate.placements?.length
      ? [PlacementActions.references({ payload: { data: candidate.placements } })]
      : [];

    return [...referenceActions(references), ...placementActions];
  }
}
